import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import { Div, Icon, Text } from '@pinpoint/design-kit';
import { charts, ThinkalikeModel } from '@pinpoint/onpoint-charts';
import DefaultTheme from 'src/styles/DefaultTheme';
import { Button } from 'src/components';
import CONSTANTS from 'src/store/constants';

export default function ClassifierMode({
  modelView: {
    model,
    chart,
    quantiles,
    availableQuantiles: { current },
    quantileIdx,
    gradient: gradientProp,
  },
  setQuantiles,
  setChart,
  setGradient: setGradientProp,
}) {
  const [gradient, setGradient] = useState(gradientProp);
  const liftScore = useMemo(
    () =>
      get(
        model,
        `modelData.dataPayload.lift_scores[${quantileIdx}]`,
        get(model, `modelData.dataPayload.liftScores[${quantileIdx}]`, false)
      ),
    [model, quantileIdx]
  );
  const axisLabelInts = useMemo(
    () => liftScore?.percentile?.every?.((pn) => Number.isSafeInteger(pn)) ?? false,
    [liftScore]
  );
  const divInnerPadding = useMemo(() => {
    if (chart === CONSTANTS.TAL_MODEL.CHARTS.LINE_CHART) return { b: '2rem', x: '1rem' };
    if (chart === CONSTANTS.TAL_MODEL.CHARTS.LINE_BAR_CHART) return { b: '0', x: '1rem' };
    return { b: '1rem', x: '1rem' };
  }, [chart]);
  const tornadoLift = liftScore ? liftScore.lift.map((x) => (x >= 1 || x === 0 ? x : -1 / x)) : [];
  const petsPercentage = get(
    model,
    'modelData.dataPayload.pets_percentage',
    get(model, 'modelData.dataPayload.petsPercentage')
  );

  useEffect(() => {
    setGradient(gradientProp);
  }, [gradientProp]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <>
      <Div d="flex" justify="space-between" data-test="div-title" m={{ t: '1rem', x: '1rem' }}>
        <Div d="flex" justify="flex-start" align="center" overflow="hidden" data-test="chart-btn-div">
          <Text textSize="title" textColor="tabSelected" style={{ fontWeight: 400 }} p={{ r: '1.5rem' }}>
            Lift Calculations
          </Text>
          {current.map((currentQuantiles) => (
            <Button
              buttonStyle="tab"
              key={`button-${currentQuantiles}`}
              selected={quantiles === currentQuantiles}
              p={{ x: '1.5em' }}
              w="1.875rem"
              data-tip={CONSTANTS.TAL_MODEL.QUANTILE_LABEL[currentQuantiles]}
              onClick={() => {
                setQuantiles(currentQuantiles);
              }}
            >
              {currentQuantiles}
            </Button>
          ))}
        </Div>
        <Div d="flex" justify="flex-end" data-test="chart-choice-btns">
          <Button
            buttonStyle="tab"
            selected={chart === CONSTANTS.TAL_MODEL.CHARTS.BAR_CHART}
            p={{ x: '1rem' }}
            data-tip="Bar chart"
            onClick={() => {
              setChart(CONSTANTS.TAL_MODEL.CHARTS.BAR_CHART);
            }}
          >
            <Icon
              name="BarChart"
              color="white"
              viewBox="0 0 50 50"
              style={{
                transform: 'rotate(270deg) scale(0.8, -0.8)',
              }}
            />
          </Button>
          <Button
            buttonStyle="tab"
            selected={chart === CONSTANTS.TAL_MODEL.CHARTS.TORNADO_BAR_CHART}
            p={{ x: '1rem' }}
            data-tip="Tornado chart"
            onClick={() => {
              setChart(CONSTANTS.TAL_MODEL.CHARTS.TORNADO_BAR_CHART);
            }}
          >
            <Icon name="VerticalBarChart" color="white" viewBox="0 0 50 50" />
          </Button>
          <Button
            buttonStyle="tab"
            selected={chart === CONSTANTS.TAL_MODEL.CHARTS.LINE_CHART}
            p={{ x: '1rem' }}
            data-tip="Line chart"
            onClick={() => {
              setChart(CONSTANTS.TAL_MODEL.CHARTS.LINE_CHART);
            }}
          >
            <Icon name="LineChart" color="white" />
          </Button>
          {petsPercentage ? (
            <Button
              buttonStyle="tab"
              selected={chart === CONSTANTS.TAL_MODEL.CHARTS.LINE_BAR_CHART}
              m={{ r: '0rem' }}
              p={{ x: '1rem' }}
              data-tip="Lift calculator"
              onClick={() => {
                setChart(CONSTANTS.TAL_MODEL.CHARTS.LINE_BAR_CHART);
              }}
            >
              <Icon
                opacity="0.8"
                pos="absolute"
                name="BarChart"
                color="white"
                style={{
                  transform: 'rotate(270deg) scale(0.8, -0.8)',
                }}
              />
              <Icon opacity="0.8" name="LineChart" color="white" />
            </Button>
          ) : null}
        </Div>
      </Div>
      {liftScore && (
        <Div p={divInnerPadding} w="100%" h="100%" overflow="hidden" data-test="div-inner">
          {chart === CONSTANTS.TAL_MODEL.CHARTS.BAR_CHART ? (
            <charts.VerticalBar
              labels={liftScore.percentile}
              series={[
                {
                  name: model.name,
                  values: liftScore.lift,
                },
              ]}
              options={{
                xAxis: {
                  name: 'Score percentile buckets',
                  axisLabel: {
                    formatter: (num) => (+num).toFixed(+!axisLabelInts),
                  },
                },
                tooltip: {
                  formatter: ({ dataIndex }) => {
                    const likelihood = tornadoLift[dataIndex];
                    return `
                  <div style="padding: 8px;">
                    Likelihood: ${likelihood.toFixed(1)}x average
                  </div>
                  `;
                  },
                },
              }}
              gradient={gradient}
              onGradientChange={(nextGradient) => {
                setGradient(nextGradient);
                setGradientProp(nextGradient);
              }}
              spreads={[liftScore.lift[liftScore.lift.length - 1] / liftScore.lift[0]]}
            />
          ) : null}
          {chart === CONSTANTS.TAL_MODEL.CHARTS.LINE_CHART ? (
            <charts.Line
              p={{ b: '1rem', t: '0', x: '0' }}
              data-test="line-chart"
              overflow="hidden"
              labels={liftScore.percentile}
              series={[
                {
                  name: model.name,
                  values: liftScore.lift.map((num) => num.toFixed(2)),
                  data: liftScore.lift.map((num) => num.toFixed(2)),
                  type: 'line',
                  lineStyle: {
                    color: DefaultTheme.colors.shade99,
                    width: 4,
                  },
                  label: {
                    formatter: (x) => `${Number(x.value).toFixed(1)}X`,
                  },
                },
              ]}
              options={{
                tooltip: {
                  formatter: ({ dataIndex: idx }) => {
                    const likelihood = Number(liftScore?.lift?.[idx]);
                    const endVal = +(liftScore?.percentile?.[idx] ?? 0);
                    const endValIdx = liftScore.percentile.findIndex((val) => val === +endVal);
                    const startVal = liftScore.percentile?.[endValIdx - 1] ?? 0;
                    return `Percentile bucket: ${startVal.toFixed(+!axisLabelInts)} - ${endVal.toFixed(
                      +!axisLabelInts
                    )} <br /> Lift: ${likelihood.toFixed(1)}`;
                  },
                },
                xAxis: {
                  name: 'Score percentile buckets',
                  nameTextStyle: {
                    fontSize: 16,
                  },
                  axisLabel: {
                    formatter: (num) => (+num).toFixed(+!axisLabelInts),
                  },
                },
              }}
              gradient={gradient}
              onGradientChange={(nextGradient) => {
                setGradient(nextGradient);
                setGradientProp(nextGradient);
              }}
            />
          ) : null}
          {chart === CONSTANTS.TAL_MODEL.CHARTS.TORNADO_BAR_CHART ? (
            <charts.VerticalBar
              labels={liftScore.percentile}
              series={[
                {
                  name: model.name,
                  values: tornadoLift,
                },
              ]}
              options={{
                grid: {
                  right: '9%',
                  left: '9%',
                },
                xAxis: {
                  name: 'Score percentile buckets',
                  axisLabel: {
                    formatter: (num) => (+num).toFixed(+!axisLabelInts),
                  },
                },
                yAxis: {
                  name: 'Likelihood of HIT',
                },
                tooltip: {
                  formatter: ({ dataIndex }) => {
                    const likelihood = tornadoLift[dataIndex];
                    return `
                  <div style="padding: 8px;">
                    Likelihood: ${likelihood.toFixed(1)}x average
                  </div>
                  `;
                  },
                },
              }}
              markLine={false}
              gradient={gradient}
              onGradientChange={(nextGradient) => {
                setGradient(nextGradient);
                setGradientProp(nextGradient);
              }}
              spreads={[liftScore.lift[liftScore.lift.length - 1] / liftScore.lift[0]]}
            />
          ) : null}
          {chart === CONSTANTS.TAL_MODEL.CHARTS.LINE_BAR_CHART ? (
            <ThinkalikeModel
              minH="auto"
              minW="100%"
              riskPercentage={petsPercentage}
              totalUsers={liftScore.total_users}
              engagedUsers={liftScore.engaged_users}
              percentile={liftScore.percentile}
              lift={liftScore.lift}
              labels={liftScore.percentile}
              options={{
                xAxis: {
                  name: 'Score percentile buckets',
                  axisLabel: {
                    formatter: (num) => (+num).toFixed(+!axisLabelInts),
                  },
                  data: liftScore.percentile,
                  axisPointer: {
                    label: {
                      formatter: ({ value }) => {
                        const endVal = +value;
                        const endValIdx = liftScore.percentile.findIndex((val) => val === +endVal);
                        const startVal = liftScore.percentile?.[endValIdx - 1] ?? 0;
                        return `${startVal.toFixed(+!axisLabelInts)} - ${endVal.toFixed(
                          +!axisLabelInts
                        )} percentiles of users`;
                      },
                    },
                  },
                },
                yAxis: {
                  name: 'HIT persons as percentage of bucket',
                },
                lineStyle: {
                  width: 5,
                  color: '#999',
                },
              }}
              gradient={gradient}
              onGradientChange={(nextGradient) => {
                setGradient(nextGradient);
                setGradientProp(nextGradient);
              }}
            />
          ) : null}
        </Div>
      )}
    </>
  );
}

ClassifierMode.propTypes = {
  modelView: PropTypes.shape({
    model: PropTypes.shape({
      name: PropTypes.string,
      dataModelType: PropTypes.string.isRequired,
      modelData: PropTypes.shape({
        dataPayload: PropTypes.shape({
          lift_scores: PropTypes.arrayOf(
            PropTypes.shape({
              percentile: PropTypes.arrayOf(PropTypes.number),
              lift: PropTypes.arrayOf(PropTypes.number),
              total_users: PropTypes.number.isRequired,
              engaged_users: PropTypes.number.isRequired,
            })
          ),
        }).isRequired,
      }),
    }),
    gradient: PropTypes.string.isRequired,
    fetchModelStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    availableQuantiles: PropTypes.arrayOf(PropTypes.number).isRequired,
    quantiles: PropTypes.number.isRequired,
    quantileIdx: PropTypes.number.isRequired,
    chart: PropTypes.string.isRequired,
  }).isRequired,
  setQuantiles: PropTypes.func.isRequired,
  setChart: PropTypes.func.isRequired,
  setGradient: PropTypes.func.isRequired,
};
